import SourceIcon from "@mui/icons-material/Source";
import { Box, Divider, Skeleton, useTheme } from "@mui/material";
import * as React from "react";

import { useNavigation } from "@/providers";
import {
  RobotoIconButton,
  RobotoTooltip,
  ViewAsOrgButton,
} from "@/shared/components";
import { SelectLayout } from "@/shared/components/visualization/controls/SelectLayout";
import { ToggleSidebar } from "@/shared/components/visualization/controls/ToggleSidebar";
import { useUnsavedChanges } from "@/shared/components/visualization/WorkspaceCtx/hooks";
import { FileRecord, IngestionStatus } from "@/shared/domain/files";
import { dirname, FileNode } from "@/shared/domain/files/filesystem";
import { usePopupState } from "@/shared/hooks";
import { actions, useVizDispatch } from "@/shared/state/visualization";

import { FileVisualizationControls, ViewFileControls } from "./controls";
import { FileHeaderDatasetLink } from "./FileHeaderDatasetLink";
import { FileHeaderPath } from "./FileHeaderPath";
import { FileNavigationToggle } from "./FileNavigationToggle";
import { UnsavedChangesDialog } from "./UnsavedChangesDialog";

interface FileHeaderProps {
  datasetId?: string;
  fileRecord?: FileRecord;
  siblingFiles?: FileNode[];
  isContentImage: boolean;
  handlePopover: (
    component: "annotations" | "details" | null,
    element: HTMLButtonElement | null,
  ) => void;
}

export const FileHeader: React.FC<FileHeaderProps> = ({
  datasetId,
  fileRecord,
  siblingFiles,
  isContentImage,
  handlePopover,
}) => {
  const theme = useTheme();
  const { goto } = useNavigation();

  const layoutMenuPopup = usePopupState();

  const unsavedChangesPopup = usePopupState();

  const hasTopics =
    fileRecord && fileRecord.ingestion_status !== IngestionStatus.NotIngested;

  const dispatchVizAction = useVizDispatch();
  const { hasUnsavedChanges } = useUnsavedChanges();

  const [pendingFileNavigation, setPendingFileNavigation] = React.useState<
    string | null
  >(null); // FileId | null

  return (
    <>
      {fileRecord ? (
        <>
          <Box
            sx={{
              marginBottom: theme.spacing(1.5),
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr) minmax(0, 0.3fr)",
                  md: "minmax(0, 1fr) minmax(0, 0.3fr)",
                  lg: "minmax(0, 1fr) minmax(0, 0.3fr) minmax(0, 1fr)",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  gap: theme.spacing(2),
                }}
              >
                <ToggleSidebar />
                <RobotoTooltip title={"Go to dataset"}>
                  <span>
                    <RobotoIconButton
                      eventName={"FileDatasetClicked"}
                      eventProperties={{
                        datasetId: datasetId ?? "",
                        fileId: fileRecord.file_id,
                      }}
                      onClick={() => {
                        goto.dataset({
                          datasetId: datasetId ?? "",
                          directoryPath: dirname(fileRecord.relative_path),
                        });
                      }}
                      sx={{
                        backgroundColor: theme.palette.action.hover,
                      }}
                    >
                      <SourceIcon
                        fontSize="small"
                        sx={{
                          color: theme.palette.text.secondary,
                          ":hover": {
                            color: theme.palette.text.primary,
                          },
                        }}
                      />
                    </RobotoIconButton>
                  </span>
                </RobotoTooltip>

                <Box
                  sx={{
                    overflow: "hidden",
                  }}
                >
                  <FileHeaderDatasetLink
                    datasetId={datasetId}
                    fileRecord={fileRecord}
                  />

                  <FileHeaderPath fileRecord={fileRecord} />
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  opacity: fileRecord ? 1 : 0,
                  transition: "opacity 0.3s",
                  justifyContent: {
                    xs: "flex-end",
                    lg: "center",
                  },
                }}
              >
                <FileNavigationToggle
                  fileRecord={fileRecord}
                  siblingFiles={siblingFiles}
                  onNavigate={(fileId) => {
                    if (hasUnsavedChanges() === false) {
                      goto.file({ fileId });
                      return;
                    }

                    setPendingFileNavigation(fileId);
                    unsavedChangesPopup.open();
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: {
                    xs: "none",
                    lg: "flex",
                  },
                  alignItems: "center",
                  justifyContent: "flex-end",
                  opacity: fileRecord ? 1 : 0,
                  transition: "opacity 0.3s",
                  gap: "15px",
                }}
              >
                <ViewAsOrgButton
                  orgId={fileRecord?.org_id}
                  size="small"
                  variant="icon"
                />
                <FileVisualizationControls
                  hide={hasTopics === true}
                  isFileAnImage={isContentImage}
                  handlePopover={handlePopover}
                />
                <ViewFileControls fileRecord={fileRecord} />
              </Box>
            </Box>
          </Box>
          <Divider />
          <SelectLayout
            anchorEl={layoutMenuPopup.anchor}
            isOpen={layoutMenuPopup.isOpen}
            onClose={layoutMenuPopup.close}
          />
          <UnsavedChangesDialog
            isOpen={unsavedChangesPopup.isOpen}
            onClose={() => {
              setPendingFileNavigation(null);
              unsavedChangesPopup.close();
            }}
            onContinueNavigation={() => {
              if (pendingFileNavigation) {
                // Discard all panels since user has confirmed to continue navigating
                dispatchVizAction(actions.removeAllPanels());
                goto.file({ fileId: pendingFileNavigation });
              }
              setPendingFileNavigation(null);
              unsavedChangesPopup.close();
            }}
          />
        </>
      ) : (
        <Skeleton
          animation="wave"
          variant="rounded"
          height={`calc(64px - ${theme.spacing(1.5)})`}
          sx={{
            mb: theme.spacing(1.5),
          }}
        />
      )}
    </>
  );
};
